import React, { useEffect, useState } from 'react'
import { getSupplierStatuses, getUser, getUsers } from '../../../api'
import {
    JsonSupplierStatus,
    JsonUser,
    SupplierStatus,
    SupplierSubstatus,
    User,
} from '../../../models'
import { getAuthUser, Roles, userHasRole } from '../../../helpers'
import defineAbilityFor from '../defineAbility'

interface FilterProps {
    updateFilters: (filterName: string, newValue: string, shouldDelete?: boolean) => void
}

export const SupplierFilters = (props: FilterProps) => {
    const { updateFilters } = props

    const ability = defineAbilityFor(getAuthUser())
    const currentUser = getAuthUser().id.toString()

    const canFilter = ability.can('Filter', 'SupplierCollections')

    const [currentUserData, setCurrentUserData] = useState<User>()
    const [collectionAgentUsers, setCollectionAgentUsers] = useState<User[]>([])
    const [collectionsSubstatuses, setCollectionsSubstatuses] = useState<SupplierSubstatus[]>([])

    useEffect(() => {
        const promises = []

        promises.push(
            getUser(currentUser).then((json) => {
                if (json.data) {
                    setCurrentUserData(json.data)
                }
            })
        )

        if (canFilter) {
            promises.push(
                getUsers().then((json) => {
                    const collectionAgentUsers = json.data
                        .map((user: JsonUser) => new User(user))
                        .filter((user: User) => userHasRole(user, Roles.CollectionAgent))
                    setCollectionAgentUsers(collectionAgentUsers)
                })
            )
        }

        if (canFilter) {
            promises.push(
                getSupplierStatuses().then(json => {
                    const supplierStatuses = json.data
                        .map((status: JsonSupplierStatus) => new SupplierStatus(status))
                    const collectionsStatus = supplierStatuses
                        .find((status: SupplierStatus) => status.code === SupplierStatus.COLLECTIONS)
                    if (collectionsStatus) {
                        setCollectionsSubstatuses(collectionsStatus.substatuses || [])
                    }
                })
            )
        }

        Promise.allSettled(promises)
    }, [currentUser, canFilter])

    return (
        <div className="row g-2">
            <div className="col-lg-4 col-xl-3">
                <label htmlFor='assignedUserFilter' className="form-label mb-1">Assigned to</label>
                <select
                    className='form-select form-select-sm'
                    id='assignedUserFilter'
                    onChange={(e) => {
                        const selectedUserId = e.target.value ?? ''
                        updateFilters('assigned_user_id', selectedUserId, selectedUserId === '-1')
                    }}
                >
                    <option key={-1} value={-1}>All</option>
                    {canFilter
                        ? collectionAgentUsers.map((user: User) => (
                            <option key={user.id} value={user.id}>{user.name}</option>)
                        ) : currentUserData && (
                            <option value={currentUserData.id}>{currentUserData.name}</option>
                        )}
                </select>
            </div>
            <div className="col-lg-4 col-xl-3">
                <label htmlFor='supplierSubstatusFilter' className="form-label mb-1">Substatus</label>
                <select
                    className='form-select form-select-sm'
                    id='supplierSubstatusFilter'
                    onChange={(e) => {
                        const selectedSubstatus = e.target.value ?? ''
                        updateFilters(
                            'supplier_substatus_code',
                            selectedSubstatus,
                            selectedSubstatus === '-1'
                        )
                    }}>
                    <option value={-1}>All</option>
                    {collectionsSubstatuses.map((substatus: SupplierSubstatus) => (
                        <option key={substatus.code} value={substatus.code}>
                            {substatus.name}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    )
}
